'use client';

import * as React from 'react';
import { useParams, usePathname } from '@/compat/navigation';
import Link from '@/compat/link';
import { Newspaper, Folder, FileText, Mail, MessageSquare, Settings } from 'lucide-react';
import { useSidebar } from '@/components/ui/sidebar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const appNavItems = [
  { icon: Newspaper, label: 'Feed', path: 'feed' },
  { icon: Folder, label: 'Projects', path: 'overview' },
  { icon: FileText, label: 'Wiki', path: 'wiki' },
  { icon: Mail, label: 'Mail', path: 'mail' },
  { icon: MessageSquare, label: 'Chat', path: 'chat' },
  { icon: Settings, label: 'Settings', path: 'settings' },
];

interface PageWithSidebarProps {
  children: React.ReactNode;
  sidebar: React.ReactNode;
  sidebarTitle?: string;
  sidebarClassName?: string;
}

export function PageWithSidebar({
  children,
  sidebar,
  sidebarTitle = 'Menu',
  sidebarClassName,
}: PageWithSidebarProps) {
  const { openMobile, setOpenMobile } = useSidebar();
  const params = useParams();
  const pathname = usePathname();
  const slug = params?.slug as string | undefined;

  const isAppActive = (path: string) => {
    if (!slug) return false;
    if (path === 'overview') {
      return pathname.includes(`/spaces/${slug}/overview`) || pathname.includes(`/spaces/${slug}/projects`);
    }
    return pathname.includes(`/spaces/${slug}/${path}`);
  };

  return (
    <div className="flex w-full h-full overflow-hidden">
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent side="left" className="w-[280px] p-0 [&>button]:hidden">
          <SheetHeader className="sr-only">
            <SheetTitle>{sidebarTitle}</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col h-full bg-muted/30">
            <div className="flex-1 overflow-auto">
              {sidebar}
            </div>
            {slug && (
              <div className="border-t p-2">
                <div className="flex justify-around">
                  {appNavItems.map((item) => {
                    const isActive = isAppActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        href={`/spaces/${slug}/${item.path}`}
                        onClick={() => setOpenMobile(false)}
                        title={item.label}
                        className={cn(
                          'flex items-center justify-center p-2 rounded-md transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )}
                      >
                        <item.icon className="h-5 w-5" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <div className={cn(
        'w-[240px] h-full border-r shrink-0 hidden lg:flex flex-col bg-muted/30',
        sidebarClassName
      )}>
        {sidebar}
      </div>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {children}
      </div>
    </div>
  );
}
